import { useEffect, useRef, useState } from 'react';
import { siteData } from '../data/mock';

export const SeriesSection = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="series"
      ref={sectionRef}
      className="relative py-20 md:py-40"
      style={{ background: '#0a0c0f' }}
    >
      {/* Top separator */}
      <div className="absolute top-0 left-5 right-5 md:left-10 md:right-10 h-px bg-gradient-to-r from-transparent via-[rgba(0,212,255,0.15)] to-transparent" />

      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        {/* Section Header */}
        <div
          className={`mb-12 md:mb-20 max-w-3xl transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#00d4ff] mb-4">
            The Series
          </p>
          <h2 className="font-montserrat font-800 text-3xl md:text-5xl uppercase text-[#e8e6e1] leading-[0.85] mb-6 md:mb-8">
            The Silent Cipher<br />
            <span className="text-[#c4a962]">Quintet</span>
          </h2>
          <p className="font-cardo text-lg text-[#888680] leading-relaxed">
            {siteData.series.description}
          </p>
        </div>

        {/* Timeline */}
        <div
          className={`transition-all duration-1000 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 md:gap-0 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-[rgba(0,212,255,0.1)]" />

            {siteData.series.books.map((book, i) => (
              <div
                key={i}
                className={`flex-1 relative transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${500 + i * 150}ms` }}
              >
                {/* Timeline dot */}
                <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      book.status === 'available'
                        ? 'bg-[#00d4ff] shadow-[0_0_12px_rgba(0,212,255,0.5)]'
                        : 'bg-[#333] border border-[#555]'
                    }`}
                  />
                </div>

                {/* Card */}
                <div
                  className={`p-6 md:p-8 border transition-colors duration-300 ${
                    book.status === 'available'
                      ? 'border-[rgba(0,212,255,0.2)] bg-[rgba(0,212,255,0.03)] hover:bg-[rgba(0,212,255,0.06)]'
                      : 'border-[rgba(255,255,255,0.04)] bg-transparent hover:bg-[rgba(255,255,255,0.02)]'
                  } ${
                    i === 0 ? '' : 'md:border-l-0'
                  }`}
                >
                  <span
                    className={`font-montserrat text-xs tracking-[0.2em] uppercase ${
                      book.status === 'available' ? 'text-[#00d4ff]' : 'text-[#555]'
                    }`}
                  >
                    Book {book.number}
                  </span>

                  <h3
                    className={`font-montserrat font-700 text-lg md:text-xl mt-3 ${
                      book.status === 'available' ? 'text-[#e8e6e1]' : 'text-[#555]'
                    }`}
                  >
                    {book.title}
                  </h3>

                  {book.status === 'available' && (
                    <span className="inline-block mt-4 font-montserrat text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-full bg-[rgba(0,212,255,0.1)] text-[#00d4ff] border border-[rgba(0,212,255,0.2)]">
                      Available Now
                    </span>
                  )}
                  {book.status === 'upcoming' && book.number === 2 && (
                    <span className="inline-block mt-4 font-montserrat text-[10px] tracking-[0.15em] uppercase px-3 py-1 rounded-full bg-[rgba(196,169,98,0.1)] text-[#c4a962] border border-[rgba(196,169,98,0.2)]">
                      Coming Soon
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming highlight */}
        <div
          className={`mt-10 md:mt-16 p-5 md:p-8 border border-[rgba(196,169,98,0.15)] bg-[rgba(196,169,98,0.02)] transition-all duration-1000 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-montserrat text-xs tracking-[0.2em] uppercase text-[#c4a962] mb-2">
            Upcoming
          </p>
          <p className="font-montserrat font-600 text-base md:text-xl text-[#e8e6e1]">
            Book Two — <span className="text-[#c4a962]">The Inheritors of Silence</span>
          </p>
        </div>
      </div>
    </section>
  );
};
